import React, { useState } from 'react';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Grow from '@material-ui/core/Grow';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import { makeStyles, styled } from '@material-ui/core/styles';
import AccountCircle from '@material-ui/icons/AccountCircle';
import ArrowDropDown from '@material-ui/icons/ArrowDropDown';
import { sizing } from '@material-ui/system';
import { colors } from '@skyslope/mache';
import { brokerManagementUrl } from '../../lib/environment-config';
import { handleAppLogout } from '../../lib/utils';

const useStyles = makeStyles(() => ({
  wrapper: {
    marginLeft: '24px',
    color: colors.blue[800],
    display: 'flex',
    alignItems: 'center',
    fontWeight: 800,
    padding: '6px 8px',
    fontSize: '16px',
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: colors.blue[50],
      borderRadius: '20px',
      outline: 'none',
    },
  },
  accountIcon: {
    fontSize: '24px',
    marginRight: '9px',
  },
  menuItem: {
    minHeight: '46px',
    '&:hover': {
      backgroundColor: colors.blue[50],
      cursor: 'pointer',
      color: '#0059DA',
    },
    '&:first-child': {
      borderTop: `2px solid ${colors.blue[800]}`,
    },
  },
}));

interface IProps {
  senderFirstName: string;
  senderLastName: string;
  preferencesUrl?: string;
  showPreferences?: boolean;
  showBrokerManagementLink?: boolean;
  user?: { isBrokerAdmin: boolean; isGroupAdmin: boolean };
  showBrokerManagement?: boolean;
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const UserMenu = (props: IProps) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const Menu = styled(MenuList)(sizing);

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleKeyDown = (event: React.KeyboardEvent<EventTarget>) => {
    if (event.key === 'Enter') {
      handleToggle();
    }
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleClose = (event: React.MouseEvent<EventTarget>) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handlePreferences = () => {
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    window.location.href =
      window.location.hostname === 'localhost' ? 'http://localhost:3000/preferences' : props.preferencesUrl;
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleBrokerManagement = () => {
    window.location.assign(`${brokerManagementUrl}?returnUrl=${encodeURIComponent(window.location.href)}`);
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const showBrokerManagementLink = () =>
    props.showBrokerManagement && props.user && (props.user.isBrokerAdmin || props.user.isGroupAdmin);

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const getBrokerManagementText = () =>
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    props.user.isBrokerAdmin ? 'Broker Management' : props.user.isGroupAdmin ? 'My Team' : null;

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <span
        className={classes.wrapper}
        onClick={handleToggle}
        onKeyDown={handleKeyDown as any}
        ref={anchorRef}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        id="userMenu"
      >
        <AccountCircle className={classes.accountIcon} />
        {props.senderFirstName} {props.senderLastName}
        <ArrowDropDown />
      </span>

      <Popper open={open} anchorEl={anchorRef.current} transition>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <Menu disablePadding width="auto">
                  {showBrokerManagementLink() ? (
                    <MenuItem
                      tabIndex={0}
                      className={classes.menuItem}
                      onClick={() => handleBrokerManagement()}
                      data-spec="my-team-link"
                    >
                      {getBrokerManagementText()}
                    </MenuItem>
                  ) : null}
                  {props.showPreferences && props.preferencesUrl ? (
                    <MenuItem tabIndex={0} className={classes.menuItem} onClick={() => handlePreferences()}>
                      Preferences
                    </MenuItem>
                  ) : null}
                  <MenuItem autoFocus tabIndex={0} className={classes.menuItem} onClick={() => handleAppLogout()}>
                    Logout
                  </MenuItem>
                </Menu>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default UserMenu;
